import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Empty, Typography, Button } from 'antd';
import { EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import RCTable from 'components/custom-table/RCTable';
import StatusHistoryModal from 'pages/trip/components/StatusHistoryModal';
import { getUIVersion } from 'utils/router-static-data';

const { Text } = Typography;

function RequestQueueTab({ tableDatas, togglePaginate, dispatchAction, onSelectionChange }) {
	const navigate = useNavigate();
	const isV2 = useMemo(() => getUIVersion() === 'new', []);
	const [statusHistoryVisible, setStatusHistoryVisible] = useState(false);
	const [selectedRequestId, setSelectedRequestId] = useState(null);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);

	const handleViewStatusHistory = (record) => {
		setSelectedRequestId(record.id);
		setStatusHistoryVisible(true);
	};

	const handleCloseStatusHistory = () => {
		setStatusHistoryVisible(false);
		setSelectedRequestId(null);
	};

	const handleView = (record) => {
		navigate(`/finance/requests/${record.id}?sourceTab=request-queue`);
	};

	const handleSelectionChange = (newSelectedRowKeys) => {
		// Prevent selecting rows with Expired status, but keep checkbox UI unchanged
		const rows = tableDatas?.[0]?.rows || [];
		const rowsById = new Map(rows.map((row) => [row.id, row]));

		const filteredKeys = newSelectedRowKeys.filter((key) => {
			const row = rowsById.get(key);
			const status = String(row?.status || '').toLowerCase();
			return status !== 'expired';
		});

		setSelectedRowKeys(filteredKeys);
		if (onSelectionChange) {
			onSelectionChange(filteredKeys);
		}
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: handleSelectionChange,
		preserveSelectedRowKeys: false,
		getCheckboxProps: (record) => {
			const status = String(record?.status || '').toLowerCase();
			const isExpired = status === 'expired';
			return isExpired
				? {
						style: {
							opacity: 0.3,
							cursor: 'not-allowed',
						},
				  }
				: {};
		},
	};

	// Reset selection when data changes
	useEffect(() => {
		setSelectedRowKeys([]);
		if (onSelectionChange) {
			onSelectionChange([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableDatas?.[0]?.rows]);

	const columns = [
		{
			title: 'Trip ID',
			dataIndex: 'trip_id',
			key: 'trip_id',
			width: 90,
			render: (trip_id) => <Text overFlow="hidden">{trip_id || '-'}</Text>,
		},
		{
			title: 'Request ID',
			dataIndex: 'adhoc_request_id',
			key: 'adhoc_request_id',
			width: 100,
		},
		{
			title: 'Request Type',
			dataIndex: 'request_type',
			key: 'request_type',
			width: 120,
			render: (text) => (
				<Text overFlow="hidden" style={{ color: '#1890ff' }}>
					{text}
				</Text>
			),
		},
		{
			title: 'Party Name',
			dataIndex: 'party_name',
			key: 'party_name',
			width: 150,
		},
		{
			title: 'Business Unit',
			dataIndex: 'business_unit_name',
			key: 'business_unit_name',
			width: 130,
		},
		{
			title: 'Request Date',
			dataIndex: 'request_date',
			key: 'request_date',
			width: 150,
			render: (date) => {
				if (!date) return '-';
				return moment(date).add(5, 'hours').add(30, 'minutes').format('DD/MM/YYYY h:mm A');
			},
		},
		{
			title: 'Request Value',
			dataIndex: 'request_value',
			key: 'request_value',
			width: 130,
			render: (value) => (value ? `₹${parseFloat(value).toLocaleString('en-IN')}` : '-'),
		},
		{
			title: 'Finance Request Status',
			dataIndex: 'status',
			key: 'status',
			width: 140,
			render: (text, record) => (
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<span
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: '8px',
						}}
					>
						<span
							style={{
								width: '8px',
								height: '8px',
								borderRadius: '50%',
								backgroundColor: '#FF8C00',
								display: 'inline-block',
							}}
						/>
						<span style={{ color: '#FF8C00' }}>{text || '-'}</span>
					</span>
					<Button
						type="text"
						icon={<InfoCircleOutlined />}
						size="small"
						onClick={() => handleViewStatusHistory(record)}
						style={{ padding: 0, height: 'auto' }}
					/>
				</div>
			),
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			width: 120,
			render: (_, record) => (
				<Button type="link" icon={<EyeOutlined />} onClick={() => handleView(record)} />
			),
		},
	];

	return (
		<>
			<div style={{ marginTop: 16 }}>
				<RCTable
					rowKey="id"
					tableDatas={tableDatas}
					togglePaginate={togglePaginate}
					dispatchAction={dispatchAction}
					columns={columns}
					rowSelection={rowSelection}
					scroll={{ x: true }}
					locale={{
						emptyText: (
							<Empty
								description={<Text type="secondary">No requests in queue</Text>}
							/>
						),
					}}
				/>
			</div>
			<StatusHistoryModal
				visible={statusHistoryVisible}
				onClose={handleCloseStatusHistory}
				adhocRequestId={selectedRequestId}
				fetchStatusHistoryAction={isV2 ? 'finance_v2' : 'finance'}
			/>
		</>
	);
}

RequestQueueTab.propTypes = {
	tableDatas: PropTypes.array.isRequired,
	togglePaginate: PropTypes.array.isRequired,
	dispatchAction: PropTypes.func.isRequired,
	onSelectionChange: PropTypes.func,
};

export default RequestQueueTab;
